/* ══════════════════════════════════════════════════════
   CALCULATIONS — Extracted from ClarityBudget.jsx
   Pure functions for financial computations.
   ══════════════════════════════════════════════════════ */

// ── Constants ──
export const MONTHS = ['January','February','March','April','May','June',
                       'July','August','September','October','November','December'];
export const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const CATS = [
  { id: 'housing',       label: 'Housing',       icon: '🏠', color: 'var(--gold)' },
  { id: 'transport',     label: 'Transport',     icon: '🚗', color: '#8FA8C8' },
  { id: 'food',          label: 'Food & Dining', icon: '🍽️', color: '#C8A08F' },
  { id: 'health',        label: 'Health',        icon: '💊', color: '#8FC89A' },
  { id: 'entertainment', label: 'Entertainment', icon: '🎬', color: '#A89FC8' },
  { id: 'shopping',      label: 'Shopping',      icon: '🛍️', color: '#C8A8B8' },
  { id: 'savings',       label: 'Savings',       icon: '💰', color: '#E8C97A' },
  { id: 'subscriptions', label: 'Subscriptions', icon: '📱', color: '#8FC8C0' },
  { id: 'utilities',     label: 'Utilities',     icon: '⚡', color: '#C8C88F' },
  { id: 'other',         label: 'Other',         icon: '📦', color: '#909090' },
];

export const CAT_MAP = Object.fromEntries(CATS.map(c => [c.id, c]));

export const STRATEGIES = {
  // WHY: the named groups must remain truthful: needs 50%, wants 30%,
  // savings 20%. Category detail can evolve without changing that contract.
  '50/30/20':    { name: '50/30/20 Rule',       desc: 'Balanced needs · moderate wants · 20% savings', color: 'var(--gold)',  allocate: i => ({ housing: i*.28, transport: i*.07, food: i*.07, utilities: i*.04, health: i*.04, entertainment: i*.10, shopping: i*.15, subscriptions: i*.05, savings: i*.20, other: i*.00 }) },
  '60/30/10':    { name: '60/30/10 Rule',       desc: '60% needs · 30% wants · 10% savings', color: '#C8A8B8',              allocate: i => ({ housing: i*.32, transport: i*.10, food: i*.10, utilities: i*.04, health: i*.04, entertainment: i*.12, shopping: i*.12, subscriptions: i*.06, savings: i*.10, other: i*.00 }) },
  '70/20/10':    { name: '70/20/10 Rule',       desc: '70% living · 20% savings · 10% giving', color: 'var(--blue)',         allocate: i => ({ housing: i*.28, transport: i*.08, food: i*.10, utilities: i*.04, health: i*.05, entertainment: i*.05, shopping: i*.06, subscriptions: i*.04, savings: i*.20, other: i*.10 }) },
  '80/20':       { name: '80/20 Pay Yourself',  desc: 'Save 20% first · live on 80%', color: '#8FC89A',                     allocate: i => ({ housing: i*.32, transport: i*.10, food: i*.12, utilities: i*.05, health: i*.05, entertainment: i*.06, shopping: i*.05, subscriptions: i*.03, savings: i*.20, other: i*.02 }) },
  '30/30/30/10': { name: '30/30/30/10 Builder', desc: '30% housing · 30% living · 30% savings · 10% giving', color: '#A89FC8', allocate: i => ({ housing: i*.30, transport: i*.06, food: i*.08, utilities: i*.04, health: i*.04, entertainment: i*.04, shopping: i*.02, subscriptions: i*.02, savings: i*.30, other: i*.10 }) },
  'zero':        { name: 'Zero-Based Budget',   desc: 'Every dollar has a job', color: 'var(--yel)',                         allocate: i => ({ housing: i*.28, transport: i*.09, food: i*.10, utilities: i*.04, health: i*.04, entertainment: i*.05, shopping: i*.06, subscriptions: i*.03, savings: i*.25, other: i*.06 }) },
  'debt':        { name: 'Debt Destroyer',       desc: 'Every extra dollar attacks debt', color: 'var(--red)',                allocate: i => ({ housing: i*.28, transport: i*.08, food: i*.09, utilities: i*.04, health: i*.04, entertainment: i*.02, shopping: i*.02, subscriptions: i*.02, savings: i*.05, other: i*.36 }) },
  'envelope':    { name: 'Envelope Method',      desc: 'Hard category caps · no borrowing', color: '#C8A08F',                allocate: i => ({ housing: i*.30, transport: i*.10, food: i*.12, utilities: i*.05, health: i*.05, entertainment: i*.04, shopping: i*.04, subscriptions: i*.03, savings: i*.22, other: i*.05 }) },
  'anti':        { name: 'Anti-Budget',          desc: 'Save first · spend freely', color: '#8FC8C0',                        allocate: i => ({ housing: i*.30, transport: i*.10, food: i*.12, utilities: i*.05, health: i*.05, entertainment: i*.08, shopping: i*.07, subscriptions: i*.03, savings: i*.20, other: i*.00 }) },
  'custom':      { name: 'Custom',               desc: 'Set your own targets', color: '#909090',                             allocate: () => null },
};

// WHY: independently rounding every category to whole dollars can create or
// destroy money. Largest-remainder allocation works in cents and guarantees
// that the stored categories add back to the exact income used by the chosen
// strategy.
export function roundStrategyAllocation(projection, income) {
  const targetCents = Math.max(0, Math.round((parseFloat(income) || 0) * 100));
  const entries = Object.entries(projection || {})
    .map(([key, value]) => ({ key, value: Math.max(0, parseFloat(value) || 0) }));
  const projectedTotal = entries.reduce((sum, entry) => sum + entry.value, 0);
  if (entries.length === 0 || projectedTotal <= 0 || targetCents <= 0) {
    return Object.fromEntries(entries.map(entry => [entry.key, 0]));
  }

  const apportioned = entries.map((entry, index) => {
    const exactCents = (entry.value / projectedTotal) * targetCents;
    const cents = Math.floor(exactCents);
    return { ...entry, index, cents, remainder: exactCents - cents };
  });
  let centsLeft = targetCents - apportioned.reduce((sum, entry) => sum + entry.cents, 0);
  [...apportioned]
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
    .forEach(entry => {
      if (centsLeft <= 0) return;
      apportioned[entry.index].cents += 1;
      centsLeft -= 1;
    });

  return Object.fromEntries(apportioned.map(entry => [entry.key, entry.cents / 100]));
}

export const PAY_SCHEDULES = [
  { id: 'monthly',     label: 'Monthly',      desc: 'Once per month' },
  { id: 'biweekly',    label: 'Bi-weekly',    desc: 'Every 2 weeks (26/yr)' },
  { id: 'semimonthly', label: 'Semi-monthly', desc: 'Twice a month' },
  { id: 'weekly',      label: 'Weekly',       desc: 'Every week (52/yr)' },
  { id: 'variable',    label: 'Variable/Gig', desc: 'Log each deposit as it comes' },
];

export function getIncomeSchedule(src = {}) {
  return src.schedule || src.frequency || 'monthly';
}

export function getIncomeScheduleIssue(src = {}, year = null, month = null) {
  const schedule = getIncomeSchedule(src);
  if (schedule === 'variable') return null;
  if (schedule === 'semimonthly') {
    const first = Number(src.payDay);
    const second = Number(src.payDay2);
    if (!Number.isInteger(first) || first < 1 || first > 31
      || !Number.isInteger(second) || second < 1 || second > 31) {
      return 'Enter both paydays (1–31).';
    }
    if (first === second) return 'Semi-monthly paydays must be different.';
    // WHY: 30 and 31 are distinct settings but both clamp to February's last
    // day. The planner keys allocations by date, so that collision would merge
    // two checks and make the split ledger ambiguous.
    if (Number.isInteger(Number(year)) && Number.isInteger(Number(month))) {
      const daysInMonth = new Date(Number(year), Number(month) + 1, 0).getDate();
      if (Math.min(first, daysInMonth) === Math.min(second, daysInMonth)) {
        return 'These paydays land on the same date this month. Choose two distinct calendar dates.';
      }
    }
    return null;
  }
  if (src.nextPayDate && Number.isFinite(parseLocalDate(src.nextPayDate).getTime())) return null;
  if (schedule === 'monthly' && Number.isInteger(Number(src.payDay))
    && Number(src.payDay) >= 1 && Number(src.payDay) <= 31) return null;
  return 'Enter the next payday so Karat can place checks on real dates.';
}

export const GOAL_ICONS = ['🎯','🏠','🚗','✈️','🎓','💍','🛡️','📱','💻','🏖️','🏋️','💰','🎸','🐕','👶'];
export const GOAL_COLORS = ['#8FC89A','#C9A96E','#8FA8C8','#A89FC8','#C8A08F','#C8A8B8','#8FC8C0','#E8C97A','#C87A7A'];

// ── Utilities ──
export const mkKey = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`;
export const uid = () => Math.random().toString(36).slice(2, 10);
export const toLocalDateStr = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const parseLocalDate = (value) => {
  if (value instanceof Date) return new Date(value.getTime());
  const match = typeof value === 'string' && value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(value);
};
export const todayStr = () => toLocalDateStr(new Date());
export const fmt = (n) => '$' + Math.abs(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtK = (n) => '$' + Math.abs(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
export const fmtSigned = (n) => {
  const amount = parseFloat(n) || 0;
  const sign = amount < 0 ? '−' : '';
  return sign + fmt(amount);
};
export const fmtKSigned = (n) => {
  const amount = parseFloat(n) || 0;
  const sign = amount < 0 ? '−' : '';
  return sign + fmtK(amount);
};
export const fmtAmount = (n) => {
  const amount = Math.abs(parseFloat(n) || 0);
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  return '$' + amount.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
};
export const pctClr = (p) => p >= 100 ? '#C87A7A' : p >= 85 ? '#E8C97A' : 'var(--green)';

export function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export function daysUntil(ds) {
  const d = parseLocalDate(ds), t = new Date();
  t.setHours(0, 0, 0, 0);
  return Math.ceil((d - t) / 864e5);
}

// ── Safe-to-Spend / Bills helpers ──

// Pure: how many dollars per remaining day given remaining budget + days left in month.
// Returns 0 for non-finite, non-positive remaining, or invalid daysLeft.
export function calcSafeToSpend(remaining, daysLeft) {
  if (!Number.isFinite(remaining) || !Number.isFinite(daysLeft) || daysLeft <= 0) return 0;
  return remaining > 0 ? remaining / daysLeft : 0;
}

// Pure: days from `today` (1..daysInMonth) to a recurring bill's `dueDay` in the same month.
// Negative means the bill is past due this month. Clamps dueDay to the actual month length.
export function getDaysUntilDue(dueDay, today, daysInMonth) {
  const dd = Math.min(parseInt(dueDay, 10) || 1, daysInMonth);
  return dd - today;
}

const moneyToCents = (value) => {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
};

const centsToMoney = (value) => value / 100;

// Pure: inclusive calendar-day boundary for the viewed month. A non-current
// month keeps the existing preview behavior and uses that month's full length.
export function getDaysLeftInMonth(year, month, now = new Date()) {
  const y = Number(year);
  const m = Number(month);
  if (!Number.isInteger(y) || !Number.isInteger(m) || m < 0 || m > 11) return 0;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const asOf = now instanceof Date && Number.isFinite(now.getTime()) ? now : new Date();
  const isCurrentMonth = asOf.getFullYear() === y && asOf.getMonth() === m;
  return isCurrentMonth
    ? Math.max(1, daysInMonth - asOf.getDate() + 1)
    : daysInMonth;
}

// Pure: every bill still unpaid for the viewed month, with month overrides
// applied. No due-date window is used because a past-due bill still reduces
// what is safe to spend.
export function getUnpaidBillsForMonth(
  recurring,
  recurringOverrides,
  year,
  month,
  monthTransactions,
) {
  const list = Array.isArray(recurring) ? recurring : [];
  const monthKey = mkKey(year, month);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const hasTransactionAuthority = Array.isArray(monthTransactions);
  const linkedBillKeys = new Set(
    (monthTransactions || [])
      .map(transaction => transaction?.linkedBillInstanceKey)
      .filter(Boolean),
  );

  return list.flatMap((bill) => {
    const instanceKey = `${bill.id}:${monthKey}`;
    const override = (recurringOverrides || {})[instanceKey];
    const markedPaid = override?.paidForMonth != null
      ? !!override.paidForMonth
      : !!bill.paid;
    // WHY: when the caller supplies the month's ledger, that ledger is the
    // money authority. A paid toggle without a linked expense has not reduced
    // cash yet, while a linked expense must not also remain reserved.
    const financiallySettled = hasTransactionAuthority
      ? linkedBillKeys.has(instanceKey)
      : markedPaid;
    if (financiallySettled || override?.skipped) return [];

    const rawAmount = override?.amount != null ? override.amount : bill.amount;
    return [{
      id: bill.id,
      name: bill.name || 'Bill',
      amount: centsToMoney(Math.max(0, moneyToCents(rawAmount))),
      dueDay: Math.min(parseInt(bill.dueDay, 10) || 1, daysInMonth),
    }];
  });
}

// WHY: reveal, dashboard hero, and explainer must consume this exact snapshot.
// Integer-cent aggregation plus one injected calendar boundary prevents bills
// or days from being independently subtracted/recomputed by each surface.
export function getSafeToSpendSnapshot({
  income,
  spent = 0,
  recurring,
  recurringOverrides,
  year,
  month,
  now = new Date(),
  transactions,
}) {
  const normalizedIncome = moneyToCents(income);
  const normalizedSpent = moneyToCents(spent);
  const remainingBeforeBillsCents = normalizedIncome - normalizedSpent;
  const unpaidBills = getUnpaidBillsForMonth(
    recurring,
    recurringOverrides,
    year,
    month,
    transactions,
  );
  const billsReservedCents = unpaidBills.reduce(
    (sum, bill) => sum + moneyToCents(bill.amount),
    0,
  );
  const remainingAfterBillsCents = remainingBeforeBillsCents - billsReservedCents;
  const daysLeft = getDaysLeftInMonth(year, month, now);
  const remainingBeforeBills = centsToMoney(remainingBeforeBillsCents);
  const billsReserved = centsToMoney(billsReservedCents);
  const remainingAfterBills = centsToMoney(remainingAfterBillsCents);

  return {
    amount: calcSafeToSpend(remainingAfterBills, daysLeft),
    income: centsToMoney(normalizedIncome),
    spent: centsToMoney(normalizedSpent),
    remainingBeforeBills,
    billsReserved,
    remainingAfterBills,
    unpaidBills,
    daysLeft,
  };
}

// Returns the list of recurring bills due in the next `daysAhead` days (default 7),
// honoring `recurringOverrides[`${id}:${monthKey}`].paidForMonth` (mirrors RecurringTab logic).
// Each result has { id, name, amount, dueDay, daysUntil }.
export function getBillsDueSoon(
  recurring,
  recurringOverrides,
  year,
  month,
  daysAhead = 7,
  monthTransactions,
) {
  const list = Array.isArray(recurring) ? recurring : [];
  if (list.length === 0) return [];
  const monthKey = mkKey(year, month);
  const hasTransactionAuthority = Array.isArray(monthTransactions);
  const linkedBillKeys = new Set(
    (monthTransactions || [])
      .map(transaction => transaction?.linkedBillInstanceKey)
      .filter(Boolean),
  );
  const today = new Date();
  const sameMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = sameMonth ? today.getDate() : 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return list
    .filter(b => {
      const instanceKey = `${b.id}:${monthKey}`;
      const ov = (recurringOverrides || {})[instanceKey];
      const markedPaid = ov?.paidForMonth != null ? !!ov.paidForMonth : !!b.paid;
      const isPaid = hasTransactionAuthority
        ? linkedBillKeys.has(instanceKey)
        : markedPaid;
      const isSkipped = !!(ov && ov.skipped);
      return !isPaid && !isSkipped;
    })
    .map(b => {
      const ov = (recurringOverrides || {})[`${b.id}:${monthKey}`];
      const amount = (ov && ov.amount != null) ? parseFloat(ov.amount) : parseFloat(b.amount) || 0;
      const dueDay = Math.min(parseInt(b.dueDay, 10) || 1, daysInMonth);
      return {
        id: b.id,
        name: b.name || 'Bill',
        amount,
        dueDay,
        daysUntil: getDaysUntilDue(dueDay, todayDate, daysInMonth),
      };
    })
    .filter(b => b.daysUntil >= -daysAhead && b.daysUntil <= daysAhead)
    .sort((a, b) => a.daysUntil - b.daysUntil);
}

// Pure: bills-aware "safe to spend today". Subtracts bills due in the next
// `daysAhead` days from `remaining` before dividing by `daysLeft`.
// Returns { amount, billsSoon, remainingAfterBills, daysLeft } so callers can
// build an explainer without re-doing the math.
export function safeToSpendToday({
  remaining,
  daysLeft,
  recurring,
  recurringOverrides,
  year,
  month,
  daysAhead = 7,
  transactions,
}) {
  const bills = getBillsDueSoon(
    recurring,
    recurringOverrides,
    year,
    month,
    daysAhead,
    transactions,
  );
  const billsSoon = bills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const remainingAfterBills = (Number(remaining) || 0) - billsSoon;
  const amount = calcSafeToSpend(remainingAfterBills, daysLeft);
  return { amount, billsSoon, remainingAfterBills, daysLeft };
}

// Pure: pick a tone + headline copy for the expected-income daily pace.
// `dailyAllowance` is the original (start-of-month) per-day budget — used to
// compare today's `safe.amount` against the user's expected pace.
//
// Tones:
//   muted  — no income or no budgets yet; CTA copy
//   red    — over budget OR bills due exceed remaining budget
//   amber  — on track but tight (today's safe < 50% of original daily pace)
//   green  — comfortable
//
// Returns { tone: 'green'|'amber'|'red'|'muted', headline, sub }.
export function safeToSpendTone({
  combinedIncome,
  remaining,
  safe,                 // shared snapshot or legacy safeToSpendToday result
  dailyAllowance,       // total monthly budget / days in month
  fmt,                  // currency formatter, e.g. fmt from this module
}) {
  const days = safe?.daysLeft || 0;
  const dayWord = days === 1 ? 'day' : 'days';
  const reservedBills = safe?.billsReserved ?? safe?.billsSoon ?? 0;
  if (!combinedIncome || combinedIncome <= 0) {
    return {
      tone: 'muted',
      // COPY-HOOK: this helper receives expected income, never available cash.
      headline: 'Add expected pay to see your daily budget pace',
      sub: '',
    };
  }
  if (remaining < 0) {
    return {
      tone: 'red',
      headline: `Over by ${fmt(-remaining)} — coast tomorrow`,
      sub: `${days} ${dayWord} left in the month`,
    };
  }
  if (safe?.remainingAfterBills < 0) {
    return {
      tone: 'red',
      headline: 'Unpaid bills exceed your remaining budget',
      sub: `${fmt(reservedBills)} in unpaid bills this month`,
    };
  }
  const ratio = dailyAllowance > 0 ? safe.amount / dailyAllowance : 1;
  if (ratio < 0.5) {
    return {
      tone: 'amber',
      headline: `Pace yourself — ${fmt(safe.amount)} for today`,
      sub: `${days} ${dayWord} left · ${fmt(reservedBills)} in unpaid bills`,
    };
  }
  return {
    tone: 'green',
    // COPY-HOOK: expected whole-month income permits pace, not a safe claim.
    headline: `Daily budget pace: ${fmt(safe.amount)} today`,
    sub: `${days} ${dayWord} left · ${fmt(reservedBills)} in unpaid bills`,
  };
}

// ── Category Guessing ──
const KW_MAP = {
  housing: ['rent','mortgage','lease','hoa','apartment'],
  transport: ['gas','uber','lyft','parking','toll','metro','bus','fuel','shell','chevron','bp','exxon'],
  food: ['grocery','groceries','whole foods','trader joe','walmart','kroger','safeway','aldi','restaurant','mcdonald','starbucks','chipotle','doordash','grubhub','ubereats','pizza','coffee','cafe','diner'],
  health: ['pharmacy','cvs','walgreens','doctor','dental','hospital','clinic','copay','medical','gym'],
  entertainment: ['netflix','hulu','spotify','apple tv','disney','movie','theater','concert','ticket'],
  shopping: ['amazon','target','costco','bestbuy','nordstrom','macy','clothing','fashion'],
  savings: ['transfer','savings','invest','401k','ira','brokerage','vanguard','fidelity'],
  subscriptions: ['subscription','monthly','prime','icloud','dropbox','adobe','microsoft','google'],
  utilities: ['electric','water','internet','phone','at&t','verizon','comcast','xfinity','pg&e','utility'],
};

export function guessCategory(desc = '') {
  const d = desc.toLowerCase();
  for (const [cat, kws] of Object.entries(KW_MAP)) {
    if (kws.some(k => d.includes(k))) return cat;
  }
  return 'other';
}

// ── Financial Calculations ──
export function calcScore(savRate, actual, budget, streak) {
  const s = Math.min(40, savRate >= 20 ? 40 : savRate >= 10 ? 20 + (savRate - 10) * 2 : Math.max(0, savRate * 2));
  let bt = 0, bc = 0;
  CATS.forEach(c => {
    const b = parseFloat(budget[c.id]) || 0;
    const a = actual[c.id] || 0;
    if (b > 0) {
      // Spending categories are ceilings; savings is a minimum goal.
      bt += c.id === 'savings'
        ? Math.min(1, Math.max(0, a) / b)
        : Math.min(1, b / Math.max(a, 0.01));
      bc++;
    }
  });
  // No budget means no adherence evidence. Awarding 40/40 here made an
  // unset behavior look earned and inflated first-run scores.
  const b = bc > 0 ? (bt / bc) * 40 : 0;
  const st = streak >= 30 ? 20 : streak >= 14 ? 16 : streak >= 7 ? 12 : streak >= 3 ? 8 : streak >= 1 ? 4 : 0;
  // WHY: the score is a 0–100 display value (graded A–F). A deeply negative
  // savings rate (spending well above income) can push `s` below 0 and yield a
  // nonsensical negative score; floor at 0 so the UI never shows one.
  return Math.max(0, Math.round(Math.min(100, s + b + st)));
}

export function scoreGrade(s) {
  if (s >= 90) return { grade: 'A', label: 'Excellent', color: 'var(--green)' };
  if (s >= 80) return { grade: 'B', label: 'Great',     color: 'var(--gold)' };
  if (s >= 65) return { grade: 'C', label: 'Good',      color: 'var(--yel)' };
  if (s >= 50) return { grade: 'D', label: 'Fair',      color: '#C8A08F' };
  return { grade: 'F', label: 'Needs Work', color: 'var(--red)' };
}

export function calcPayoffMonths(bal, apr, pay) {
  if (bal <= 0) return 0;
  if (pay <= 0) return 999;
  const r = apr / 100 / 12;
  if (r === 0) return Math.ceil(bal / pay);
  if (pay <= bal * r) return 999;
  const m = Math.ceil(-Math.log(1 - (r * bal / pay)) / Math.log(1 + r));
  return (isFinite(m) && !isNaN(m)) ? Math.max(m, 1) : 999;
}

export function calcEmergencyMonths(assets, monthlyExpenses) {
  const savings = (assets || [])
    .filter(a => a?.type === 'cash' || a?.type === 'savings' || (a.label || '').toLowerCase().match(/sav|emergency|cash|checking/))
    .reduce((s, a) => s + (parseFloat(a.value) || 0), 0);
  if (monthlyExpenses > 0) return savings / monthlyExpenses;
  return null;
}

const nonNegativeMoney = (value) => {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? Math.max(0, amount) : 0;
};

// WHY: every net-worth surface must use the same asset/liability boundary.
// A configured Mortgage Tracker contributes the home's full current value as
// an asset and its balance as a debt. Legacy property-equity rows become
// ambiguous in that mode, so they are disclosed and excluded until the user
// marks them as a separate property's equity.
export function getNetWorthSnapshot({
  nw,
  cards = [],
  debts = [],
  mortgage = null,
} = {}) {
  const assets = Array.isArray(nw?.assets) ? nw.assets : [];
  const legacyDebts = Array.isArray(nw?.debts) ? nw.debts : [];
  const personalDebts = Array.isArray(debts) ? debts : [];
  const creditCards = Array.isArray(cards) ? cards : [];

  const mortgageBalance = nonNegativeMoney(mortgage?.balance);
  const mortgagePropertyValue = nonNegativeMoney(mortgage?.propertyValue);
  const hasMortgageRecord = Boolean(mortgage && (mortgageBalance > 0 || mortgagePropertyValue > 0));

  const excludedPropertyAssets = hasMortgageRecord
    ? assets.filter(asset => asset?.type === 'property' && asset?.propertyScope !== 'other-equity')
    : [];
  const countedAssets = hasMortgageRecord
    ? assets.filter(asset => asset?.type !== 'property' || asset?.propertyScope === 'other-equity')
    : assets;

  const manualAssets = countedAssets.reduce((sum, asset) => sum + nonNegativeMoney(asset?.value), 0);
  const manualDebts = legacyDebts.reduce((sum, debt) => sum + nonNegativeMoney(debt?.value ?? debt?.balance), 0);
  const loanDebts = personalDebts.reduce((sum, debt) => sum + nonNegativeMoney(debt?.balance ?? debt?.value), 0);
  const cardDebts = creditCards.reduce((sum, card) => sum + nonNegativeMoney(card?.balance), 0);
  const homeAsset = hasMortgageRecord ? mortgagePropertyValue : 0;
  const homeDebt = hasMortgageRecord ? mortgageBalance : 0;
  const totalAssets = manualAssets + homeAsset;
  const totalDebts = manualDebts + loanDebts + cardDebts + homeDebt;

  return {
    totalAssets,
    totalDebts,
    netWorth: totalAssets - totalDebts,
    countedAssets,
    excludedPropertyAssets,
    components: {
      manualAssets,
      manualDebts,
      loanDebts,
      cardDebts,
      homeAsset,
      homeDebt,
    },
    propertyMode: hasMortgageRecord ? 'mortgage-full-value' : 'manual-equity',
    propertyValueMissing: hasMortgageRecord && mortgagePropertyValue <= 0,
  };
}

// WHY: runway and long-range projections need a full-month spending basis,
// not a partial current-month total that grows every day. Prefer the user's
// configured spending budget, then a completed-month average, then recurring
// bills. Savings transfers are excluded from burn.
export function getMonthlySpendingBasis({
  txns = {},
  budget = {},
  recurring = [],
  year,
  month,
  now = new Date(),
} = {}) {
  const budgetAmount = CATS
    .filter(category => category.id !== 'savings')
    .reduce((sum, category) => sum + nonNegativeMoney(budget?.[category.id]), 0);
  if (budgetAmount > 0) {
    return { amount: budgetAmount, kind: 'budget', label: 'configured monthly spending budget' };
  }

  const selectedStart = new Date(Number(year), Number(month), 1);
  const currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const firstOffset = selectedStart < currentStart ? 0 : 1;
  const completedTotals = [];
  for (let offset = firstOffset; offset < firstOffset + 3; offset++) {
    const d = new Date(Number(year), Number(month) - offset, 1);
    const rows = Array.isArray(txns?.[mkKey(d.getFullYear(), d.getMonth())])
      ? txns[mkKey(d.getFullYear(), d.getMonth())]
      : [];
    if (rows.length === 0) continue;
    completedTotals.push(rows.reduce((sum, transaction) => {
      if (transaction?.category === 'savings') return sum;
      return sum + nonNegativeMoney(transaction?.amount);
    }, 0));
  }
  if (completedTotals.length > 0) {
    return {
      amount: completedTotals.reduce((sum, amount) => sum + amount, 0) / completedTotals.length,
      kind: 'completed-average',
      label: `${completedTotals.length}-month completed spending average`,
    };
  }

  const recurringAmount = (Array.isArray(recurring) ? recurring : [])
    .reduce((sum, bill) => sum + nonNegativeMoney(bill?.amount), 0);
  if (recurringAmount > 0) {
    return { amount: recurringAmount, kind: 'recurring', label: 'recurring monthly bills' };
  }

  return { amount: 0, kind: 'none', label: 'no full-month spending basis yet' };
}

export function calcDebtFreeMonths(cards, debts, mortgage) {
  let mx = 0;
  (cards || []).forEach(c => {
    const m = calcPayoffMonths(parseFloat(c.balance) || 0, parseFloat(c.apr) || 0, parseFloat(c.minPayment) || 0);
    mx = Math.max(mx, m);
  });
  (debts || []).forEach(d => {
    const m = calcPayoffMonths(parseFloat(d.balance) || 0, parseFloat(d.rate) || 0, parseFloat(d.payment) || 0);
    mx = Math.max(mx, m);
  });
  if (mortgage?.balance) {
    const m = calcPayoffMonths(parseFloat(mortgage.balance) || 0, parseFloat(mortgage.rate) || 0, parseFloat(mortgage.payment) || 0);
    mx = Math.max(mx, m);
  }
  return mx;
}

export function calcSavingsProjection(monthlySavings, years, r = 0.07) {
  const monthly = Number(monthlySavings);
  const n = Math.max(0, Number(years) * 12);
  const annualRate = Number(r);
  if (!Number.isFinite(monthly) || monthly <= 0 || !Number.isFinite(n) || n <= 0) return 0;
  if (!Number.isFinite(annualRate)) return 0;
  const mr = annualRate / 12;
  if (mr === 0) return monthly * n;
  return monthly * ((Math.pow(1 + mr, n) - 1) / mr);
}

export function goalStats(g) {
  const target = Math.max(0, parseFloat(g?.target) || 0);
  const current = Math.max(0, parseFloat(g?.current) || 0);
  const done = target > 0 && current >= target;
  const remaining = Math.max(0, target - current);
  if (done) {
    return {
      monthlyNeeded: 0,
      monthsLeft: 0,
      remaining: 0,
      done: true,
      pace: 'complete',
      deadlineState: 'complete',
    };
  }
  if (!g?.deadline) {
    return {
      monthlyNeeded: 0,
      monthsLeft: null,
      remaining,
      done: false,
      pace: 'flexible',
      deadlineState: 'none',
    };
  }
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const dl = parseLocalDate(g.deadline);
  const daysLeft = Math.ceil((dl - now) / 864e5);
  if (daysLeft < 0) {
    return {
      monthlyNeeded: 0,
      monthsLeft: 0,
      remaining,
      done: false,
      pace: 'deadline-passed',
      deadlineState: 'overdue',
    };
  }
  const ml = Math.max(1 / 30.44, daysLeft / 30.44);
  const mn = Math.ceil(remaining / ml);
  const contribs = g.contributions || [];
  let pace = 'on-pace';
  if (contribs.length >= 2) {
    const sorted = [...contribs].sort((a, b) => a.date.localeCompare(b.date));
    const first = parseLocalDate(sorted[0].date);
    const last = parseLocalDate(sorted[sorted.length - 1].date);
    const moSpan = Math.max(1, (last - first) / 864e5 / 30.44);
    const totalC = contribs.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    const avgMo = totalC / moSpan;
    pace = avgMo >= mn * 0.9 ? 'on-pace' : avgMo >= mn * 0.6 ? 'slightly-behind' : 'behind';
  }
  return {
    monthlyNeeded: mn,
    monthsLeft: daysLeft === 0 ? 0 : Math.max(1, Math.ceil(ml)),
    remaining,
    done,
    pace,
    deadlineState: daysLeft === 0 ? 'today' : 'future',
  };
}

// ── Income Calculations ──
export function getPayDatesInMonth(src, yr, mo) {
  const sched = getIncomeSchedule(src);
  const amt = parseFloat(src.amount) || 0;
  const anchor = src.nextPayDate ? parseLocalDate(src.nextPayDate) : null;
  const mStart = new Date(yr, mo, 1);
  const mEnd = new Date(yr, mo + 1, 0);
  const dates = [];
  const mk2 = mkKey(yr, mo);
  const daysThisMonth = mEnd.getDate();
  const payDate = (day) => `${mk2}-${String(Math.min(Math.max(1, parseInt(day, 10) || 1), daysThisMonth)).padStart(2, '0')}`;
  const shiftDays = (date, amount) => {
    const shifted = new Date(date);
    shifted.setDate(shifted.getDate() + amount);
    return shifted;
  };
  const amountForDate = date => src.adjustments?.[date] ?? amt;

  if (sched === 'variable') {
    return (src.deposits || [])
      .filter(d => d.date && d.date.startsWith(mk2))
      .map(d => ({ date: d.date, amount: parseFloat(d.amount) || 0 }));
  }
  if (getIncomeScheduleIssue(src, yr, mo)) return [];
  if (sched === 'monthly') {
    const dt = payDate(src.payDay || anchor?.getDate());
    dates.push({ date: dt, amount: amountForDate(dt) });
  } else if (sched === 'semimonthly') {
    const d1 = payDate(src.payDay);
    const d2 = payDate(src.payDay2 || 15);
    dates.push(
      { date: d1, amount: amountForDate(d1) },
      { date: d2, amount: amountForDate(d2) },
    );
  } else if (sched === 'biweekly' || sched === 'weekly') {
    const anch = anchor || new Date(yr, mo, 1);
    const interval = sched === 'biweekly' ? 14 : 7;
    let d = new Date(anch);
    d.setHours(0, 0, 0, 0);
    while (d > mStart) d = shiftDays(d, -interval);
    while (d <= mEnd) {
      if (d >= mStart) {
        const ds = toLocalDateStr(d);
        dates.push({ date: ds, amount: amountForDate(ds) });
      }
      d = shiftDays(d, interval);
    }
  } else {
    dates.push({ date: mk2 + '-01', amount: amt });
  }
  return dates;
}

export function getExpectedThisMonth(src, yr, mo) {
  const dates = getPayDatesInMonth(src, yr, mo);
  if (dates.length > 0) {
    return dates.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  }
  const amount = Math.max(0, parseFloat(src?.amount) || 0);
  const schedule = getIncomeSchedule(src);
  // A date is required to place individual checks, but monthly-frequency
  // income still has a truthful full-month amount for dashboard totals.
  if (schedule === 'monthly') return amount;
  if (schedule === 'semimonthly') return amount * 2;
  return 0;
}

export function getDepositedThisMonth(src, yr, mo) {
  const today = todayStr();
  return getPayDatesInMonth(src, yr, mo)
    .filter(p => p.date <= today)
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
}

// Compute per-paycheck split allocations for each recurring bill, across
// arbitrary cross-month funding cycles.
//
// A bill instance is one occurrence of a recurring bill (e.g., the Jan 3 rent
// vs the Feb 3 rent). Each instance has a funding cycle (priorDueDate,
// currentDueDate] — half-open on the left so a paycheck never belongs to two
// cycles simultaneously. Paychecks within the cycle window can fund the bill,
// regardless of which calendar month they fall in.
//
// Caller passes `paychecks` spanning at least the visible month plus the
// adjacent prior + next months so cross-month cycles see all relevant checks.
// The function emits up to 2 instances per bill — one with dueDate in the
// visible month, one with dueDate in the next month — and the planner picks
// the right instance per visible paycheck via cycle membership.
//
// Output shape: { '<billId>:<dueDate>': { ... } }
//   billId             the bill's id
//   dueDate            absolute YYYY-MM-DD this instance is due
//   amount             effective bill amount for this instance (post-override)
//   splitGroup         [checkDate] the bill is split across (cycle window)
//   perCheck           { [checkDate]: number } — user edit OR auto suggestion
//   funded             sum of perCheck values
//   remaining          amount - funded (negative = over-funded)
//   nextUnfundedDate   first auto-suggested check date in splitGroup, or null
//   paid               bill marked paid for the dueDate's month
//   skipped            bill skipped for the dueDate's month (UI hides the row)
export function computeBillSplits(recurring, paychecks, allocations, monthKey, recurringOverrides) {
  const result = {};
  const checks = Array.isArray(paychecks) ? paychecks : [];
  const allocs = allocations || {};
  const overs = recurringOverrides || {};
  if (!Array.isArray(recurring) || checks.length === 0) return result;

  const [vYr, vMo] = monthKey.split('-').map(n => parseInt(n, 10)); // vMo is 1-indexed
  // Helpers: clamp dueDay to days-in-month, format YYYY-MM-DD, walk months.
  const dim = (yr, mo) => new Date(yr, mo, 0).getDate(); // mo 1-indexed → last day of `mo`
  const fmtDate = (yr, mo, day) => `${yr}-${String(mo).padStart(2, '0')}-${String(Math.min(parseInt(day, 10) || 1, dim(yr, mo))).padStart(2, '0')}`;
  const mkOf = (yr, mo) => `${yr}-${String(mo).padStart(2, '0')}`;
  const prevMonth = (yr, mo) => mo === 1 ? [yr - 1, 12] : [yr, mo - 1];
  const nextMonth = (yr, mo) => mo === 12 ? [yr + 1, 1] : [yr, mo + 1];

  // Two candidate instances per bill: due in visible month, due in next month.
  // Both can have cycles that touch the visible month (the next-month instance's
  // cycle starts at the visible-month due date and runs forward).
  const [nYr, nMo] = nextMonth(vYr, vMo);
  const candidates = [
    { yr: vYr, mo: vMo },
    { yr: nYr, mo: nMo },
  ];

  for (const r of recurring) {
    if (!r || !r.id) continue;
    for (const cand of candidates) {
      const candMk = mkOf(cand.yr, cand.mo);
      const ov = overs[`${r.id}:${candMk}`] || {};
      const skipped = !!ov.skipped;
      const paid = ov.paidForMonth != null ? !!ov.paidForMonth : !!r.paid;
      const amount = (ov.amount != null) ? (parseFloat(ov.amount) || 0) : (parseFloat(r.amount) || 0);

      const dueDate = fmtDate(cand.yr, cand.mo, r.dueDay);
      const [pYr, pMo] = prevMonth(cand.yr, cand.mo);
      const priorDate = fmtDate(pYr, pMo, r.dueDay);
      const instanceKey = `${r.id}:${dueDate}`;

      if (skipped || amount <= 0) {
        // Emit a placeholder so the UI can render skipped state when relevant,
        // but only for the visible-month instance (next-month is never shown
        // in the planner if its cycle is fully outside the visible month).
        if (cand.yr === vYr && cand.mo === vMo) {
          result[instanceKey] = { billId: r.id, dueDate, amount, splitGroup: [], perCheck: {}, funded: 0, remaining: amount, nextUnfundedDate: null, paid, skipped };
        }
        continue;
      }

      // Cycle window: (priorDate, dueDate] — half-open left, closed right.
      const group = checks
        .filter(c => c.date > priorDate && c.date <= dueDate)
        .map(c => c.date);

      // Skip instances that have no in-scope paycheck — they wouldn't render.
      if (group.length === 0) continue;

      // Skip instances whose cycle doesn't touch the visible month at all
      // (avoids surfacing far-out next-month bills when no current-month
      // paycheck is in their cycle).
      const touchesVisible = group.some(d => d.startsWith(monthKey));
      if (!touchesVisible) continue;

      const editKey = `bill_${r.id}`;
      const edits = {};
      let editsSum = 0;
      for (const d of group) {
        const v = allocs[d]?.[editKey];
        if (v != null) {
          const n = parseFloat(v) || 0;
          edits[d] = n;
          editsSum += n;
        }
      }

      const unedited = group.filter(d => !(d in edits));
      const remainingToSplit = amount - editsSum;
      const perUnedited = unedited.length > 0 ? Math.max(0, remainingToSplit / unedited.length) : 0;

      const perCheck = {};
      let funded = 0;
      let nextUnfundedDate = null;
      for (const d of group) {
        const v = (d in edits) ? edits[d] : perUnedited;
        perCheck[d] = v;
        funded += v;
        if (!(d in edits) && nextUnfundedDate == null && v > 0) nextUnfundedDate = d;
      }

      result[instanceKey] = {
        billId: r.id,
        dueDate,
        amount,
        splitGroup: group,
        perCheck,
        funded,
        remaining: amount - funded,
        nextUnfundedDate,
        paid,
        skipped,
      };
    }
  }

  return result;
}

// ── Income Drift Helpers ──

// Pure: total expected monthly income across all sources for a given (year, month).
// Wraps getExpectedThisMonth so callers don't have to map+reduce themselves.
export function getTotalMonthlyIncome(incomes, yr, mo) {
  if (!Array.isArray(incomes)) return 0;
  return incomes.reduce((s, src) => s + getExpectedThisMonth(src, yr, mo), 0);
}

// Pure: 3-month rolling average of expected monthly income, anchored on (yr, mo)
// going backward (yr/mo, yr/mo-1, yr/mo-2). For users with `schedule: 'variable'`
// or `incomeType: '1099'`, this smooths out single-month spikes/dips.
// `windowMonths` defaults to 3. Returns 0 if `incomes` is not an array.
export function getRollingIncomeAvg(incomes, yr, mo, windowMonths = 3) {
  if (!Array.isArray(incomes) || incomes.length === 0) return 0;
  const w = Math.max(1, windowMonths);
  let total = 0;
  for (let i = 0; i < w; i++) {
    let m = mo - i;
    let y = yr;
    while (m < 0) { m += 12; y -= 1; }
    total += getTotalMonthlyIncome(incomes, y, m);
  }
  return total / w;
}

// Pure: returns true if any income source is variable / 1099 (so the drift
// detector should compare against a rolling average, not the current month).
export function hasVariableIncome(incomes) {
  if (!Array.isArray(incomes)) return false;
  return incomes.some(s => s?.schedule === 'variable' || s?.incomeType === '1099');
}

// Pure: given the budget metadata `_meta` (or undefined) and the user's
// current income for comparison, decide whether to show the drift banner.
//
// Returns:
//   { show: false }                          — no banner needed
//   { show: true, from, to, strategy }       — banner should render with these values
//
// `current` is the income value the caller has already chosen (current month
// total OR rolling average, depending on hasVariableIncome).
//
// Threshold: 5% drift from `incomeAtApply`. If user previously clicked
// "Keep current", `_meta.dismissedAtIncome` is set; we re-suppress until
// drift exceeds 5% from THAT dismissal point too.
export function computeIncomeDrift(meta, current) {
  if (!meta || !meta.strategy || !Number.isFinite(meta.incomeAtApply) || meta.incomeAtApply <= 0) {
    return { show: false };
  }
  if (!Number.isFinite(current) || current <= 0) return { show: false };

  const baseline = Number.isFinite(meta.dismissedAtIncome) && meta.dismissedAtIncome > 0
    ? meta.dismissedAtIncome
    : meta.incomeAtApply;

  const drift = Math.abs(current - baseline) / baseline;
  if (drift <= 0.05) return { show: false };

  return {
    show: true,
    from: meta.incomeAtApply,
    to: current,
    strategy: meta.strategy,
  };
}

// ── CSV Parsing / Writing ──
export function parseCSV(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  const source = String(text ?? '');

  const pushCell = () => {
    row.push(cell.trim());
    cell = '';
  };
  const pushRow = () => {
    pushCell();
    if (row.some(value => value !== '')) rows.push(row);
    row = [];
  };

  for (let i = 0; i < source.length; i++) {
    const char = source[i];
    if (char === '"') {
      if (inQuotes && source[i + 1] === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      pushCell();
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && source[i + 1] === '\n') i++;
      pushRow();
    } else {
      cell += char;
    }
  }
  if (cell !== '' || row.length > 0) pushRow();
  return rows;
}

export function parseCSVLine(line) {
  return parseCSV(line)[0] || [];
}

export function escapeCSVCell(value) {
  const numeric = typeof value === 'number' && Number.isFinite(value);
  let text = value == null ? '' : String(value);
  // Quoting alone does not stop spreadsheet formula execution. Preserve real
  // numeric cells, but neutralize user-authored text before quote escaping.
  if (!numeric && /^[\t ]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function serializeCSV(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map(row => (Array.isArray(row) ? row : []).map(escapeCSVCell).join(','))
    .join('\r\n');
}

export function normalizeDate(d) {
  if (!d) return todayStr();
  const m1 = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m1) {
    const [, mo, da, yr] = m1;
    const y = yr.length === 2 ? '20' + yr : yr;
    return `${y}-${mo.padStart(2, '0')}-${da.padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  try { return new Date(d).toISOString().slice(0, 10); } catch { return todayStr(); }
}

// ── Daily Quotes ──
export const DAILY_QUOTES = [
  { text: 'Do not save what is left after spending, but spend what is left after saving.', author: 'Warren Buffett' },
  { text: "It's not how much money you make, but how much money you keep.", author: 'Robert Kiyosaki' },
  { text: 'The habit of saving is itself an education; it fosters every virtue.', author: 'T.T. Munger' },
  { text: "The rich invest their money and spend what's left. The poor spend and invest what's left.", author: 'Jim Rohn' },
  { text: 'An investment in knowledge pays the best interest.', author: 'Benjamin Franklin' },
  { text: 'Wealth consists not in having great possessions, but in having few wants.', author: 'Epictetus' },
  { text: 'Money is a terrible master but an excellent servant.', author: 'P.T. Barnum' },
  { text: "A budget is telling your money where to go instead of wondering where it went.", author: 'Dave Ramsey' },
  { text: 'The goal isn\'t more money. The goal is living life on your terms.', author: 'Chris Brogan' },
  { text: 'Earn with your mind, not your time.', author: 'Naval Ravikant' },
  { text: 'Wealth is largely the result of habit.', author: 'John Jacob Astor' },
  { text: 'Discipline is the bridge between goals and accomplishment.', author: 'Jim Rohn' },
  { text: 'Small daily improvements over time lead to stunning results.', author: 'Robin Sharma' },
  { text: 'Success is nothing more than a few simple disciplines, practiced every day.', author: 'Jim Rohn' },
  { text: 'The secret of getting ahead is getting started.', author: 'Mark Twain' },
];

export function getTodayQuote() {
  const d = new Date();
  const idx = (d.getFullYear() * 366 + d.getMonth() * 31 + d.getDate()) % DAILY_QUOTES.length;
  return DAILY_QUOTES[idx];
}

// ── Card Sorting & Colors ──
export const CARD_COLORS = ['#4A90D9', '#C9A96E', '#8FC89A', '#C87A7A', '#A89FC8', '#C8A08F', '#8FC8C0'];
export function avalancheOrder(cards) { return [...cards].sort((a, b) => parseFloat(b.apr) - parseFloat(a.apr)); }
export function snowballOrder(cards) { return [...cards].sort((a, b) => parseFloat(a.balance) - parseFloat(b.balance)); }

// ── Next Pay Date ──
export function getNextPayDate(src) {
  const today = new Date();
  const td = today.getDate(), m = today.getMonth(), y = today.getFullYear();
  if (src.schedule === 'monthly') {
    const pd = src.payDay || 1;
    const target = td < Math.min(pd, new Date(y, m + 1, 0).getDate())
      ? new Date(y, m, Math.min(pd, new Date(y, m + 1, 0).getDate()))
      : new Date(y, m + 1, Math.min(pd, new Date(y, m + 2, 0).getDate()));
    return toLocalDateStr(target);
  }
  if (src.schedule === 'semimonthly') {
    const pd1 = src.payDay || 1, pd2 = src.payDay2 || 15;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const first = Math.min(pd1, lastDay);
    const second = Math.min(pd2, lastDay);
    if (td < first) return toLocalDateStr(new Date(y, m, first));
    if (td < second) return toLocalDateStr(new Date(y, m, second));
    const nextLastDay = new Date(y, m + 2, 0).getDate();
    return toLocalDateStr(new Date(y, m + 1, Math.min(pd1, nextLastDay)));
  }
  if (!src.nextPayDate) return null;
  const anchor = parseLocalDate(src.nextPayDate);
  const interval = src.schedule === 'biweekly' ? 14 : src.schedule === 'weekly' ? 7 : null;
  if (!interval) return null;
  let d = new Date(anchor);
  d.setHours(0, 0, 0, 0);
  const todayLocal = new Date(y, m, td);
  while (d <= todayLocal) d.setDate(d.getDate() + interval);
  return toLocalDateStr(d);
}

// ── Mortgage Calculations ──
export function validateMortgagePaymentBreakdown({
  principalAndInterest,
  totalWithEscrow,
  principalAndInterestConfirmed,
} = {}) {
  const payment = Number(principalAndInterest);
  const hasTotal = totalWithEscrow !== '' && totalWithEscrow != null;
  const totalPayment = hasTotal ? Number(totalWithEscrow) : null;
  if (!Number.isFinite(payment) || payment <= 0) {
    return { error: 'Enter the monthly principal + interest payment from your mortgage statement.' };
  }
  if (!principalAndInterestConfirmed) {
    return { error: 'Confirm that this is principal + interest, not the total payment with taxes or insurance.' };
  }
  if (hasTotal && (!Number.isFinite(totalPayment) || totalPayment <= 0)) {
    return { error: 'Enter a valid total payment with escrow, or leave it blank.' };
  }
  if (hasTotal && totalPayment < payment) {
    return { error: 'The total payment with escrow cannot be less than principal + interest.' };
  }
  return {
    payment,
    totalPayment,
    paymentType: 'principal-interest',
    error: null,
  };
}

export function calcMortgage(balance, rate, payment, extraMonthly = 0) {
  const startingBalance = parseFloat(balance);
  const annualRate = parseFloat(rate);
  const basePayment = parseFloat(payment);
  const extraPayment = parseFloat(extraMonthly || 0);
  if (!Number.isFinite(startingBalance) || startingBalance <= 0 ||
      !Number.isFinite(annualRate) || annualRate < 0 ||
      !Number.isFinite(basePayment) || basePayment <= 0 ||
      !Number.isFinite(extraPayment) || extraPayment < 0) return null;

  const monthlyRate = annualRate / 100 / 12;
  let bal = startingBalance;
  let months = 0;
  let totalInterest = 0;
  const effectivePayment = basePayment + extraPayment;
  const firstMonthInterest = bal * monthlyRate;

  if (effectivePayment <= firstMonthInterest) {
    return {
      months: Infinity,
      years: Infinity,
      remMonths: 0,
      totalInterest: Infinity,
      finalBalance: bal,
      canPayOff: false,
      reason: 'payment-below-interest',
      minimumPayment: firstMonthInterest,
    };
  }

  while (bal > 0 && months < 600) {
    const interest = bal * monthlyRate;
    const principal = effectivePayment - interest;
    totalInterest += interest;
    bal = Math.max(0, bal - principal);
    months++;
  }
  const canPayOff = bal <= 0;
  return {
    months: canPayOff ? months : Infinity,
    years: canPayOff ? Math.floor(months / 12) : Infinity,
    remMonths: canPayOff ? months % 12 : 0,
    totalInterest: canPayOff ? totalInterest : Infinity,
    finalBalance: bal,
    canPayOff,
    reason: canPayOff ? null : 'term-exceeds-limit',
    minimumPayment: firstMonthInterest,
  };
}

export function calcPayoffScenarios(balance, rate, payment) {
  const base = calcMortgage(balance, rate, payment, 0);
  if (!base) return null;
  return {
    base,
    extra100: calcMortgage(balance, rate, payment, 100),
    extra200: calcMortgage(balance, rate, payment, 200),
    extra500: calcMortgage(balance, rate, payment, 500),
    biweekly: calcMortgage(balance, rate, payment, payment / 12),
  };
}

export function calcExtraForYearsSooner(balance, rate, payment, yearsSooner) {
  if (!balance || !rate || !payment) return null;
  const base = calcMortgage(balance, rate, payment, 0);
  if (!base || !base.canPayOff) return null;
  const targetMonths = base.months - (yearsSooner * 12);
  if (targetMonths <= 0) return null;
  let lo = 0, hi = 10000, extra = 0;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    const r = calcMortgage(balance, rate, payment, mid);
    if (!r || !r.canPayOff) { lo = mid; continue; }
    if (r.months <= targetMonths) { hi = mid; extra = mid; }
    else lo = mid;
  }
  return Math.ceil(extra);
}

// ── Self-Employment Tax ──
export const SE_TAX_RATE = 0.153;
export const SS_RATE = 0.124;
export const MEDICARE_RATE = 0.029;
export const SS_CAP_2026 = 184500;
export const QUARTERLY_DUE_DATES = ['04-15', '06-15', '09-15', '01-15'];

export function calcSETax(netIncome) {
  if (netIncome <= 0) return { seTax: 0, socialSecurity: 0, medicare: 0, taxableBase: 0 };
  const taxableBase = netIncome * 0.9235;
  const socialSecurity = Math.min(taxableBase, SS_CAP_2026) * SS_RATE;
  const medicare = taxableBase * MEDICARE_RATE;
  // 0.9% Additional Medicare Tax. NOTE: this assumes a single filer ($200k
  // threshold); MFJ ($250k) / MFS ($125k) are not modeled. This is an estimate
  // to size a tax reserve, not a filed return.
  const additionalMedicare = taxableBase > 200000 ? (taxableBase - 200000) * 0.009 : 0;
  return {
    seTax: socialSecurity + medicare + additionalMedicare,
    socialSecurity,
    medicare: medicare + additionalMedicare,
    taxableBase,
  };
}

export function calcRecommendedReserve(monthlyProfit, incomeTaxRate = 0.12) {
  if (monthlyProfit <= 0) return 0;
  const { seTax } = calcSETax(monthlyProfit * 12);
  const monthlySE = seTax / 12;
  const monthlyIncomeTax = monthlyProfit * incomeTaxRate;
  return Math.round(monthlySE + monthlyIncomeTax);
}

export function calcQuarterlyEstimate(ytdProfit) {
  if (ytdProfit <= 0) return 0;
  const { seTax } = calcSETax(ytdProfit);
  const estimatedIncomeTax = ytdProfit * 0.12;
  return Math.round((seTax + estimatedIncomeTax) / 4);
}

export function getNextQuarterlyDue() {
  const today = new Date();
  const year = today.getFullYear();
  const dates = [
    new Date(year, 3, 15), new Date(year, 5, 15),
    new Date(year, 8, 15), new Date(year + 1, 0, 15),
  ];
  const next = dates.find(d => d > today) || dates[0];
  const daysUntil = Math.ceil((next - today) / (1000 * 60 * 60 * 24));
  // WHY: format from local Y/M/D, not toISOString(). These dates are built at
  // LOCAL midnight; toISOString() converts to UTC, which shifts the due date
  // back a day (e.g. Sep 15 -> Sep 14) for any user east of UTC — a wrong tax
  // deadline that contradicts QUARTERLY_DUE_DATES. Local formatting is stable.
  const pad = (n) => String(n).padStart(2, '0');
  const date = `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
  return { date, daysUntil };
}

// ── Cash Pulse helpers ──
// The ledger stores each change as { id, delta, balance, date: 'YYYY-MM-DD', note }.
// Helpers below derive momentum (delta over N days) and a sparkline series
// (one data point per day, carrying the last known balance forward).

export function getCashDelta(ledger, days = 7) {
  if (!Array.isArray(ledger) || ledger.length === 0) return null;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = toLocalDateStr(cutoff);
  const inWindow = ledger.filter(e => e && e.date && e.date >= cutoffStr);
  if (inWindow.length === 0) return null;
  return inWindow.reduce((sum, e) => sum + (Number(e.delta) || 0), 0);
}

export function getCashSparkline(ledger, days = 7) {
  if (!Array.isArray(ledger) || ledger.length === 0) return [];
  const sorted = [...ledger].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const out = [];
  let carry = 0;
  const first = sorted[0];
  if (first && Number.isFinite(first.balance)) {
    carry = first.balance - (Number(first.delta) || 0);
  }
  let cursor = 0;
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const dateStr = toLocalDateStr(d);
    while (cursor < sorted.length && sorted[cursor].date <= dateStr) {
      if (Number.isFinite(sorted[cursor].balance)) carry = sorted[cursor].balance;
      cursor++;
    }
    out.push({ date: dateStr, balance: carry });
  }
  return out;
}
